import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faFileContract,
  faFileInvoice,
  faUsers,
  faCashRegister,
  faCalendarAlt,
  faRocket
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const SubscriptionModal = ({ isOpen, onClose, subscription, upcomingSubscription, t }) => {
  if (!isOpen) return null;

  const checkCorporate = (row, key) => {
    return row?.subscriptionId === 5 && row?.[key] === 0 ? '\u221E' : row?.[key] ?? '-';
  };

  const getUsagePercent = (used, total) => {
    if (total === 0 || total === '\u221E') return 0;
    return Math.min(100, Math.round((used / total) * 100));
  };

  const daysRemaining = subscription ? moment(subscription.endDate).diff(moment(), 'days') : 0;
  const totalDays = subscription ? moment(subscription.endDate).diff(moment(subscription.startDate), 'days') : 1;
  const daysUsedPercent = Math.max(0, 100 - Math.round((daysRemaining / totalDays) * 100));

  const statsConfig = [
    {
      key: 'invoices',
      icon: faFileInvoice,
      label: t("home.totalEinvoices"),
      used: subscription?.sentInvoices ?? 0,
      total: checkCorporate(subscription, "availableInvoices"),
      color: 'teal'
    },
    {
      key: 'users',
      icon: faUsers,
      label: t("subscription.totalUsers"),
      used: subscription?.usedUsers ?? 0,
      total: checkCorporate(subscription, "totalUsers"),
      color: 'blue'
    },
    {
      key: 'tcrs',
      icon: faCashRegister,
      label: t("subscription.totalTcrs"),
      used: subscription?.usedTcrs ?? 0,
      total: checkCorporate(subscription, "totalTcrs"),
      color: 'purple'
    },
    {
      key: 'days',
      icon: faCalendarAlt,
      label: t("subscription.daysRemaining"),
      value: daysRemaining,
      suffix: t("subscription.days") || "days",
      color: 'orange',
      percent: daysUsedPercent
    }
  ];

  return (
    <div
      className={`subscription-modal__overlay ${isOpen ? 'subscription-modal__overlay--visible' : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="subscription-modal__content">
        <div className="subscription-modal__header">
          <div className="subscription-modal__header-text">
            <h2>{t("subscription.subscription")}</h2>
            {subscription && (
              <span className="subscription-modal__header-plan">{subscription.subscriptionName}</span>
            )}
          </div>
          <button className="subscription-modal__close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="subscription-modal__body">
          {subscription ? (
            <>
              <div className="subscription-modal__plan-badge-container">
                <span className="subscription-modal__plan-badge subscription-modal__plan-badge--active">
                  <span className="subscription-modal__plan-badge-dot"></span>
                  {t("subscription.active")}
                </span>
              </div>

              <div className="subscription-modal__dates">
                <div className="subscription-modal__stat subscription-modal__stat--teal">
                  <div className="subscription-modal__stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="subscription-modal__stat-info">
                    <div className="subscription-modal__stat-label">{t("subscription.startDate")}</div>
                    <div className="subscription-modal__stat-value">{moment(subscription.startDate).format("DD/MM/YYYY")}</div>
                  </div>
                </div>
                <div className="subscription-modal__stat subscription-modal__stat--orange">
                  <div className="subscription-modal__stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="subscription-modal__stat-info">
                    <div className="subscription-modal__stat-label">{t("subscription.endDate")}</div>
                    <div className="subscription-modal__stat-value">{moment(subscription.endDate).format("DD/MM/YYYY")}</div>
                  </div>
                </div>
              </div>

              <div className="subscription-modal__stats">
                {statsConfig.map((stat) => (
                  <div key={stat.key} className={`subscription-modal__stat subscription-modal__stat--${stat.color}`}>
                    <div className="subscription-modal__stat-icon">
                      <FontAwesomeIcon icon={stat.icon} />
                    </div>
                    <div className="subscription-modal__stat-info">
                      <div className="subscription-modal__stat-label">{stat.label}</div>
                      <div className="subscription-modal__stat-value">
                        {stat.value !== undefined ? (
                          <>{stat.value} <small>{stat.suffix}</small></>
                        ) : (
                          <>{stat.used} <span className="subscription-modal__stat-separator">/</span> {stat.total}</>
                        )}
                      </div>
                      <div className="subscription-modal__stat-progress">
                        <div
                          className="subscription-modal__stat-progress-bar"
                          style={{ width: `${stat.percent ?? getUsagePercent(stat.used, stat.total)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {upcomingSubscription && (
                <div className="subscription-modal__upcoming">
                  <div className="subscription-modal__upcoming-icon">
                    <FontAwesomeIcon icon={faRocket} />
                  </div>
                  <div className="subscription-modal__upcoming-info">
                    <span className="subscription-modal__upcoming-label">{t("subscription.upcoming")}</span>
                    <span className="subscription-modal__upcoming-value">
                      {upcomingSubscription.subscriptionName} - {moment(upcomingSubscription.startDate).format("DD/MM/YYYY")}
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="subscription-modal__empty">
              <FontAwesomeIcon icon={faFileContract} />
              <p>{t("subscription.noSubscriptions")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
